import React, { useEffect, useState } from "react";
import { withAuthenticationRequired } from "../context/AuthContext";
import Loading from "../components/Loading";
import {
  Button,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Tab,
  Tabs,
  TextField
} from "@mui/material";
import { useRequestHandler } from "../utils/useRequestHandler";
import Servicios from "../services";
import { formatearFechaDDMMYYHHMM } from "../utils/dates";

// Enums.FeedbackEstado del backend.
export const ESTADO = { Nuevo: 0, Respondido: 1, Cerrado: 2 };
const NOMBRE_ESTADO = { 0: "Nuevo", 1: "Respondido", 2: "Cerrado" };
const TODAS = "__todas__";
const TAMANIO_PAGINA = 50;

/**
 * M1-80 / TRA-O1-53: bandeja del feedback que llega por "Ayudanos a mejorar"
 * (POST Usuario/Feedback). Antes se leía con una consulta a mano a
 * FeedbackUsuario. Pestañas por estado, filtros por categoría y fecha, y cada
 * ítem se marca como nuevo, respondido o cerrado. Responder es por fuera
 * (mail o WhatsApp al contacto que dejó la persona); acá solo se registra.
 */
export const FeedbackBandeja = () => {
  const sendRequest = useRequestHandler();
  const [estado, setEstado] = useState(ESTADO.Nuevo);
  const [categoria, setCategoria] = useState(TODAS);
  const [desde, setDesde] = useState("");
  const [hasta, setHasta] = useState("");
  const [pagina, setPagina] = useState(1);
  const [datos, setDatos] = useState(null);

  const cargar = () => {
    sendRequest(
      Servicios.ObtenerFeedback,
      [
        {
          estado,
          categoria: categoria === TODAS ? undefined : categoria,
          desde: desde || undefined,
          hasta: hasta || undefined,
          pagina,
          tamanioPagina: TAMANIO_PAGINA
        }
      ],
      data => setDatos(data?.data ?? null)
    );
  };

  useEffect(() => {
    cargar();
  }, [estado, categoria, desde, hasta, pagina]);

  const cambiarEstado = (id, nuevo) => {
    sendRequest(Servicios.CambiarEstadoFeedback, [id, nuevo], () => cargar());
  };

  const porEstado = datos?.porEstado ?? {};
  const items = datos?.items ?? [];
  const total = datos?.total ?? 0;
  const paginas = Math.max(1, Math.ceil(total / TAMANIO_PAGINA));

  const filtrar = setter => valor => {
    setPagina(1);
    setter(valor);
  };

  return (
    <div className="card-modern">
      <div className="page-header">
        <h4 className="page-title">Feedback</h4>
      </div>
      <hr className="border-gray-200 dark:border-gray-700 my-4" />

      <Tabs value={estado} onChange={(_, v) => filtrar(setEstado)(v)} aria-label="Estado del feedback">
        {Object.values(ESTADO).map(e => (
          <Tab key={e} value={e} label={`${NOMBRE_ESTADO[e]} (${porEstado[e] ?? 0})`} />
        ))}
      </Tabs>

      <div className="flex flex-wrap gap-3 my-4">
        <FormControl size="small" style={{ minWidth: 200 }}>
          <InputLabel id="feedback-categoria-label">Categoría</InputLabel>
          <Select
            labelId="feedback-categoria-label"
            label="Categoría"
            value={categoria}
            inputProps={{ "aria-label": "Categoría" }}
            onChange={e => filtrar(setCategoria)(e.target.value)}
          >
            <MenuItem value={TODAS}>Todas</MenuItem>
            {(datos?.categorias ?? []).map(c => (
              <MenuItem key={c || "sin"} value={c}>
                {c || "Sin categoría"}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          size="small"
          type="date"
          label="Desde"
          InputLabelProps={{ shrink: true }}
          value={desde}
          onChange={e => filtrar(setDesde)(e.target.value)}
        />
        <TextField
          size="small"
          type="date"
          label="Hasta"
          InputLabelProps={{ shrink: true }}
          value={hasta}
          onChange={e => filtrar(setHasta)(e.target.value)}
        />
      </div>

      {datos && items.length === 0 && <p>No hay feedback con estos filtros.</p>}

      <div className="flex flex-col gap-3">
        {items.map(f => (
          <article
            key={f.id}
            className="border border-gray-200 dark:border-gray-700 rounded p-3"
            aria-label={`Feedback ${f.id}`}
          >
            <div className="flex flex-wrap items-center gap-2 mb-2 text-sm">
              <strong>#{f.id}</strong>
              <span>{formatearFechaDDMMYYHHMM(f.fechaHoraCreacion)}</span>
              <Chip size="small" label={f.categoria || "Sin categoría"} />
              <Chip size="small" variant="outlined" label={NOMBRE_ESTADO[f.estado]} />
              {f.plataforma && <span>{f.plataforma}</span>}
              {f.appVersion && <span>v{f.appVersion}</span>}
              {f.flavor && f.flavor !== "prod" && <Chip size="small" color="warning" label={f.flavor} />}
              {f.pantallaOrigen && <span>desde {f.pantallaOrigen}</span>}
            </div>
            <p style={{ whiteSpace: "pre-wrap" }} className="mb-2">
              {f.mensaje}
            </p>
            <div className="text-sm mb-2">
              Contacto: <span>{f.contacto || "(no dejó)"}</span>
              {" · "}
              {f.usuarioId ? `Cuenta #${f.usuarioId}${f.usuarioNombre ? ` (${f.usuarioNombre})` : ""}` : "Sin sesión"}
            </div>
            <div className="flex gap-2">
              {f.estado !== ESTADO.Respondido && (
                <Button size="small" variant="outlined" onClick={() => cambiarEstado(f.id, ESTADO.Respondido)}>
                  Marcar respondido
                </Button>
              )}
              {f.estado !== ESTADO.Cerrado && (
                <Button size="small" variant="outlined" onClick={() => cambiarEstado(f.id, ESTADO.Cerrado)}>
                  Cerrar
                </Button>
              )}
              {f.estado !== ESTADO.Nuevo && (
                <Button size="small" onClick={() => cambiarEstado(f.id, ESTADO.Nuevo)}>
                  Volver a nuevo
                </Button>
              )}
            </div>
          </article>
        ))}
      </div>

      {paginas > 1 && (
        <div className="flex items-center gap-3 mt-4">
          <Button size="small" disabled={pagina <= 1} onClick={() => setPagina(pagina - 1)}>
            Anterior
          </Button>
          <span>
            Página {pagina} de {paginas}
          </span>
          <Button size="small" disabled={pagina >= paginas} onClick={() => setPagina(pagina + 1)}>
            Siguiente
          </Button>
        </div>
      )}
    </div>
  );
};

export default withAuthenticationRequired(FeedbackBandeja, {
  onRedirecting: () => <Loading />
});
